import type { ComponentType, SVGProps } from 'react';
import {
    ArrowRightCircleIcon,
    BeakerIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    HandThumbUpIcon,
    LifebuoyIcon,
    RectangleStackIcon,
    UserIcon,
} from '@heroicons/react/24/outline';

import type { FilMenage, MessageMenage, ReponseRapide } from '../api/useMessagesMenage';
import { nomPersonne } from '../../types/housekeepingMessaging';
import { langueMobile, t } from '../i18n/langue';

/** Un picto par réponse rapide (Q13) : il porte le sens pour qui lit mal. */
export const PICTOS_REPONSES: Record<ReponseRapide, ComponentType<SVGProps<SVGSVGElement>>> = {
    need_linen: RectangleStackIcon,
    need_products: BeakerIcon,
    room_done: CheckCircleIcon,
    guest_in_room: UserIcon,
    problem: ExclamationTriangleIcon,
    on_my_way: ArrowRightCircleIcon,
    ok_seen: HandThumbUpIcon,
    need_help: LifebuoyIcon,
};

/**
 * Le titre d'un fil pour qui le lit : sa boîte pour la personne de terrain
 * (« Gouvernance »), la personne pour qui tient la boîte (« Nino K. · Technique »).
 */
export function titreFil(fil: FilMenage, lecteurId: number | null | undefined): string {
    if (fil.kind === 'team') return t('messagesMenage.equipe');
    if (fil.kind === 'inter_facility') return fil.counterpart?.name ?? '—';

    const boite = fil.box ? t(`messagesMenage.boites.${fil.box}`) : '';
    if (fil.member?.id === lecteurId) return boite;

    return fil.box === 'gouvernance' ? nomPersonne(fil.member, '—') : `${nomPersonne(fil.member, '—')} · ${boite}`;
}

/** Ce qu'un message dit, en une ligne, dans la langue du lecteur. */
export function apercuMessage(message: MessageMenage | null | undefined): string {
    if (!message) return '';
    if (message.hidden) return t('messagesMenage.bulle.masque');
    if (message.quick_reply_key) return t(`messagesMenage.rapides.${message.quick_reply_key}`);
    if (message.text) return message.text;
    if (message.audio_url) return '🎤';

    return message.photo_url ? t('messagesMenage.bulle.photo') : '';
}


/**
 * Lecture à voix haute par la synthèse du téléphone (Q10), gratuite.
 * `false` quand le téléphone n'a aucune voix pour la langue (fréquent en géorgien) :
 * l'écran le dit au lieu de lire avec un accent qui rendrait le texte faux.
 */
export function lireAVoixHaute(texte: string, langue: string = langueMobile()): boolean {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return false;

    const voix = window.speechSynthesis.getVoices().find((v) => v.lang.toLowerCase().startsWith(langue));
    if (!voix) return false;

    const enonce = new SpeechSynthesisUtterance(texte);
    enonce.voice = voix;
    enonce.lang = voix.lang;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(enonce);

    return true;
}
